/**
 * List-B → InboxMessage mapper (P3 lens merge).
 *
 * Maps a canonical notes/email `Message` (messageModel.ts, from List B —
 * `projects/{vp}/messages`) onto the `InboxMessage` view-model the REAL
 * Team-Inbox <Thread/> renders. WhatsApp comes from List A already in
 * InboxMessage shape; this brings notes + email into the SAME timeline so a
 * single chronological merge feeds one Thread.
 *
 * Channel markers reuse the existing bubble/badge treatment (NOT a new one):
 *   · internal note (channel:'portal') → kind:'note' → yellow bubble + the
 *     "Internal" tag, and ticks are suppressed (`m.kind !== 'note'`), so a note
 *     is NEVER rendered as if it were sent to the client.
 *   · email (channel:'email') → a normal in/out text bubble carrying the
 *     ✉ Email badge (`isEmail`).
 */
import type { Message } from '../../../lib/messageModel';
import type { InboxMessage } from '../lib/types';
import type { WorkspaceMessage } from '../../../lib/messagesStore';

/** Chronological sort key: server Timestamp when resolved, else localTs. */
export function listBAtMs(m: Message): number {
  const ts = m.createdAt as unknown as { toMillis?: () => number } | null;
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof m.localTs === 'number') return m.localTs;
  return Date.now();
}

/** Map ONE List-B message (note · email · system · draft) to the Thread view-model. */
export function listBToInboxMessage(m: Message): InboxMessage {
  const raw = m as unknown as {
    authorRole?: string;
    kind?: string;
    systemEvent?: unknown;
    isAiDraft?: unknown;
    draftStatus?: unknown;
  };
  const isEmail = m.channel === 'email';
  // System-authored docs — postMilestone() events (kind:'milestone'), systemEvent
  // rows, and the authorRole:'system' AI-draft docs — render as the Thread's
  // NEUTRAL system line (Vera gate 2026-07-09). This keeps them visible (they
  // were shown before) WITHOUT mislabeling a milestone as an internal note or
  // showing an unsent AI draft as a sent bubble.
  const isSystem =
    raw.authorRole === 'system' ||
    raw.kind === 'milestone' ||
    raw.systemEvent != null;
  const who: InboxMessage['who'] =
    m.authorRole === 'client' ? 'client' : m.authorRole === 'ai' ? 'ai' : 'human';
  const att = m.attachments && m.attachments[0];
  return {
    id: m.id,
    direction: m.direction,
    who,
    // system/milestone/draft → system line · email → text + ✉ badge ·
    // internal note → 'note' (Internal tag, no ticks — never shown as
    // client-sent) · everything else (staff/portal chat) → a normal bubble,
    // exactly as the baseline ProjectThreadPanel rendered it.
    kind: isSystem ? 'system' : isEmail ? 'text' : m.internal ? 'note' : 'text',
    body: m.body || '',
    at: listBAtMs(m),
    deliveryState: m.deliveryState ?? null,
    attachment: att
      ? {
          kind: att.type,
          fileName: att.name,
          contentType: att.mimeType,
          downloadURL: att.url,
        }
      : null,
    transcript: null,
    templateName: null,
    isEmail,
    contactId: m.contactId || null,
    waMessageId: null,
    reactions: null,
    location: null,
    // Escape hatch: a note/email is NOT a WhatsApp transport row. The Thread
    // only reads `.raw` for WA-specific affordances (reactions target wamid,
    // media viewer) — none of which apply to a note/email bubble. Intentional.
    raw: m as unknown as WorkspaceMessage,
  };
}
